import type { EnvironmentId, ThreadId } from "@cmmd-forge/contracts";

import { PULL_REQUEST_STATE_PRESENTATION } from "../pullRequest/pullRequestIcons";

import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import { resolveEnvModeLabel, resolveLockedWorkspaceLabel } from "../BranchToolbar.logic";
import type { DraftThreadEnvMode } from "../../composerDraftStore";

/**
 * The rules behind the controls strip welded under the Composer.
 *
 * The strip has two modes and one shape. On the new-thread landing the
 * Environment, worktree mode and branch controls are live pickers over the
 * Draft. On an existing thread the same three are settled facts: the worktree
 * is checked out, the branch is on disk, the turn already runs where it runs,
 * and Forge has no command to move any of them. This module holds the pure
 * half of both modes so the component stays a rendering.
 *
 * Note `execution` is the *execution target* (a cloud Environment vs this
 * device), not `RuntimeMode`. In Forge `RuntimeMode` is the permission mode
 * (approval-required / auto-accept-edits / full-access) and lives in the
 * Composer footer; the two are unrelated and must not be conflated.
 */
export interface ComposerControlsStripTrigger {
  readonly id: "execution" | "worktree" | "branch";
  /** What the trigger prints. */
  readonly label: string;
  /** What a screen reader reads, so the value is never announced bare. */
  readonly ariaLabel: string;
  readonly kind: "cloud" | "device" | "worktree" | "pendingWorktree" | "checkout" | "branch";
}

export interface ChatComposerContextStripInput {
  readonly executionLabel: string | null;
  readonly isCloudExecution: boolean;
  readonly projectTitle: string | null;
  readonly branch: string | null;
  readonly worktreePath: string | null;
  /**
   * Which workspace the thread asked for, which is not the same question as
   * which one it has. A thread started in worktree mode carries no
   * `worktreePath` until the server's first turn creates the worktree, so the
   * path alone cannot tell a local thread apart from a worktree thread that is
   * still waiting on one.
   */
  readonly envMode: DraftThreadEnvMode | null;
}

/**
 * The facts an existing thread gives the strip, derived from the thread.
 *
 * `executionLabel` is `null`, not "Local", for a plain local thread. t3code
 * suppresses the environment indicator on the primary environment for the same
 * reason: "runs on this machine" is the default, and a control restating it is
 * noise that also makes the strip's visibility rule unfalsifiable — with a
 * label always present, `shouldShowChatComposerContextStrip` collapses to "has
 * a project" and the branch and worktree conditions can never fire.
 *
 * No environment display name either: it lives in the environment store, which
 * the Composer does not read, and a wrong-but-specific label is worse than a
 * right-but-coarse one.
 */
export function buildChatComposerContextFacts(input: {
  readonly environmentId: EnvironmentId;
  readonly projectTitle: string | null;
  readonly branch: string | null;
  readonly worktreePath: string | null;
  readonly envMode: DraftThreadEnvMode | null;
}): ChatComposerContextStripInput {
  const isCloudExecution = classifyEnvironmentId(input.environmentId).isCloud;
  return {
    executionLabel: isCloudExecution ? "Cloud" : null,
    isCloudExecution,
    projectTitle: input.projectTitle,
    branch: input.branch,
    worktreePath: input.worktreePath,
    envMode: input.envMode,
  };
}

/**
 * Mirrors t3code's `shouldShowComposerContextStrip`: a project is required, and
 * beyond that the strip needs at least one fact worth stating. A strip carrying
 * nothing but the project name repeats the breadcrumb and buys a notch cut out
 * of the composer for nothing.
 */
export function shouldShowChatComposerContextStrip(input: ChatComposerContextStripInput): boolean {
  if (!hasText(input.projectTitle)) return false;
  return hasText(input.branch) || hasText(input.worktreePath) || hasText(input.executionLabel);
}

/**
 * Ordered the way the landing orders its pickers, which is where it runs, then
 * which checkout, then which branch. The two modes read as the same strip that
 * way, so the eye does not re-learn it across the new-thread to thread
 * transition.
 *
 * No project entry. t3code's strip carries Environment, worktree mode and
 * branch only; the project is named by the breadcrumb on a thread and by the
 * hero headline on the landing, and a third place to say it is a third place
 * for the three to disagree.
 */
export function buildComposerControlsStripTriggers(
  input: ChatComposerContextStripInput,
): readonly ComposerControlsStripTrigger[] {
  const triggers: ComposerControlsStripTrigger[] = [];

  if (hasText(input.executionLabel)) {
    const label = input.executionLabel.trim();
    triggers.push({
      id: "execution",
      label,
      ariaLabel: `Run on: ${label}`,
      kind: input.isCloudExecution ? "cloud" : "device",
    });
  }
  // Always emitted, never conditional on having a worktree. Where the turn
  // runs is a fact in its own right, so a strip that drops the workspace when a
  // thread happens to run in the checkout is the reported "worktree display is
  // missing in a real thread".
  //
  // Three states, not two, the way t3code's `BranchToolbar` reads them. A path
  // present is a worktree. No path but worktree mode is a worktree the server
  // has not created yet, and calling that "Local checkout" contradicts the
  // choice the user just made. Anything else runs in the checkout.
  const workspace = hasText(input.worktreePath)
    ? { label: resolveLockedWorkspaceLabel(input.worktreePath), kind: "worktree" as const }
    : input.envMode === "worktree"
      ? { label: resolveEnvModeLabel("worktree"), kind: "pendingWorktree" as const }
      : {
          label: input.envMode === null ? "Workspace" : resolveLockedWorkspaceLabel(null),
          kind: "checkout" as const,
        };
  triggers.push({
    id: "worktree",
    label: workspace.label,
    ariaLabel: `Workspace: ${workspace.label}`,
    kind: workspace.kind,
  });
  if (hasText(input.branch)) {
    const label = input.branch.trim();
    triggers.push({ id: "branch", label, ariaLabel: `Branch: ${label}`, kind: "branch" });
  }

  return triggers;
}

// Labels collapse to icons when the strip's content no longer fits. A small
// hysteresis on the way back out keeps the boundary from flapping. Lifted from
// t3code's `BranchToolbar.logic`, where the Resting Composer's controls share
// the strip with these labels and the two negotiate for the same row.
const CONTEXT_STRIP_COMPACT_EXPAND_HYSTERESIS_PX = 16;

export function resolveContextStripLabelsCompact(input: {
  readonly compact: boolean;
  readonly neededWidth: number;
  readonly availableWidth: number;
}): boolean {
  return input.compact
    ? input.neededWidth > input.availableWidth - CONTEXT_STRIP_COMPACT_EXPAND_HYSTERESIS_PX
    : input.neededWidth > input.availableWidth;
}

function hasText(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * The pull request the thread's branch is attached to, shown as a badge beside
 * the branch trigger. Colour is the state, ported from t3code's
 * `prStatusIndicator` and derived from the shared `PULL_REQUEST_STATE_PRESENTATION`:
 * merged and closed are both "not open" but mean opposite things, and a single
 * muted treatment for both loses the distinction the colour exists to carry.
 *
 * "pending" is a linked pull request whose state nothing has answered for
 * yet: the thread's durable link carries the number and URL, and the composer
 * says so rather than inventing a state. Adopted from t3code's pending badge.
 */
export type ChatComposerPrState = "open" | "closed" | "merged" | "pending";

export interface ChatComposerPrBadge {
  readonly number: number;
  readonly url: string;
  readonly colorClass: string;
  readonly label: string;
  readonly tooltip: string;
}

export interface ChatComposerPrInput {
  readonly number: number | null;
  readonly state: ChatComposerPrState | null;
  readonly url: string | null;
  readonly title: string | null;
}

const PR_STATE_COLOR: Record<ChatComposerPrState, string> = {
  open: PULL_REQUEST_STATE_PRESENTATION.open.toneClassName,
  closed: PULL_REQUEST_STATE_PRESENTATION.closed.toneClassName,
  merged: PULL_REQUEST_STATE_PRESENTATION.merged.toneClassName,
  // Nothing has answered for a linked pull request yet, so there is no host state to borrow.
  pending: "text-muted-foreground",
};

/**
 * `null` unless every part is present. A badge is a link — a number with no URL
 * is a control that looks clickable and is not, which is worse than no badge.
 */
export function buildChatComposerPrBadge(input: ChatComposerPrInput): ChatComposerPrBadge | null {
  if (input.number === null || input.state === null || !hasText(input.url)) return null;

  const state = input.state;
  const stateLabel = `${state.charAt(0).toUpperCase()}${state.slice(1)}`;
  const lead = `PR #${input.number} - ${stateLabel}`;

  return {
    number: input.number,
    url: input.url.trim(),
    colorClass: PR_STATE_COLOR[state],
    label: `Pull request #${input.number}, ${stateLabel.toLowerCase()}`,
    tooltip: hasText(input.title) ? `${lead}: ${input.title.trim()}` : lead,
  };
}

export interface RemoteComposerThreadGoal {
  readonly goal: string | null;
  readonly taskManaged: boolean;
  readonly onUpdateGoal: (goal: string | null) => Promise<void>;
}

export function resolveComposerThreadGoal(input: {
  readonly activeThread: { readonly goal?: string | null; readonly anchorKind: string } | undefined;
  readonly activeThreadId: ThreadId | null;
  readonly activeThreadEnvironmentId: EnvironmentId | undefined;
  readonly isServerThread: boolean;
  readonly remoteGoal?: RemoteComposerThreadGoal | null;
}) {
  const { activeThread, activeThreadId, activeThreadEnvironmentId, isServerThread, remoteGoal } =
    input;
  if (!activeThreadId || !activeThreadEnvironmentId || !isServerThread) return null;
  if (remoteGoal)
    return { ...remoteGoal, environmentId: activeThreadEnvironmentId, threadId: activeThreadId };
  if (!activeThread) return null;
  return {
    goal: activeThread.goal,
    environmentId: activeThreadEnvironmentId,
    threadId: activeThreadId,
    taskManaged: activeThread.anchorKind === "task",
  };
}
